import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChatChannel } from '@/features/chat/services/chat.service'
import { useMeetingAnnotationStore } from '../store/meeting-annotation.store'
import type { AnnotationView, LiveChunk, Stroke } from '../services/annotation-geometry'
import { readLive, readStroke, readView, toWireStroke } from '../services/annotation-wire'

/**
 * Annotation on one presented document, kept in step with the room.
 *
 * The room (RoomCoordinator) holds the finished strokes; this mirrors them in
 * the annotation store and sends this client's own. On a document reaching the
 * stage, and again after every reconnect, it asks the room for the document's
 * ink (`annotation:get`) — that is what catches a late joiner up. From then on
 * the `annotation:*` events keep it current.
 *
 * `supported` is null until the room has answered once, and false when the
 * server predates annotation (it answers `unknown_event`): the caller hides the
 * tools rather than offer a pen whose ink goes nowhere.
 *
 * `view` is the presenter's position in the document, for viewers who follow.
 */
export function useAnnotations({
  doc,
  selfId,
  channel,
}: {
  /** The presentation's id as the room knows it, or null when none. */
  doc: string | null
  /** This client's peer id — the `by` the server stamps on its strokes. */
  selfId: string | null
  channel: ChatChannel | null
}) {
  const [supported, setSupported] = useState<boolean | null>(null)
  const [view, setView] = useState<AnnotationView | null>(null)
  const docRef = useRef(doc)
  docRef.current = doc

  // Catch up: on a new document, and on every (re)join — `channel` is a new
  // object each time the room channel is joined.
  useEffect(() => {
    setView(null)
    if (!doc || !channel?.request) return
    let cancelled = false
    void channel.request('annotation:get', { shareId: doc }).then(({ status, response }) => {
      if (cancelled) return
      if (status === 'ok') {
        setSupported(true)
        const strokes = Array.isArray(response.strokes)
          ? response.strokes.map(readStroke).filter((s): s is Stroke => s !== null)
          : []
        useMeetingAnnotationStore.getState().replaceAll(doc, strokes)
        setView(readView(response.view))
      } else if (status === 'error' && response.reason === 'unknown_event') {
        setSupported(false)
      }
      // A timeout or a dropped send leaves things as they are; the next
      // reconnect asks again.
    })
    return () => {
      cancelled = true
    }
  }, [doc, channel])

  useEffect(() => {
    if (!channel) return
    const store = useMeetingAnnotationStore.getState
    const offs = [
      channel.subscribe('annotation:stroke', (payload) => {
        const m = payload as { shareId?: unknown; stroke?: unknown } | null
        const stroke = readStroke(m?.stroke)
        if (typeof m?.shareId !== 'string' || !stroke) return
        store().receive(m.shareId, stroke)
        store().liveDrop(m.shareId, stroke.id)
      }),
      channel.subscribe('annotation:erased', (payload) => {
        const m = payload as { shareId?: unknown; ids?: unknown } | null
        if (typeof m?.shareId !== 'string' || !Array.isArray(m.ids)) return
        store().remove(m.shareId, m.ids.filter((id): id is string => typeof id === 'string'))
      }),
      channel.subscribe('annotation:live', (payload) => {
        const live = readLive(payload)
        if (live) store().liveAppend(live.shareId, live.by, live.chunk, Date.now())
      }),
      // Only the document's presenter can move this — the server refuses
      // anyone else — so whoever it came from, it is the presenter's.
      channel.subscribe('share:view', (payload) => {
        const m = payload as { shareId?: unknown } | null
        if (m?.shareId !== docRef.current) return
        const next = readView(payload)
        if (next) setView(next)
      }),
    ]
    // Often enough that a laser vanishes promptly when its owner stops.
    const sweep = window.setInterval(() => store().liveExpire(Date.now()), 250)
    return () => {
      offs.forEach((off) => off())
      window.clearInterval(sweep)
    }
  }, [channel])

  /** Send a finished stroke. Taken back locally if the room refuses it. */
  const draw = useCallback(
    (stroke: Stroke) => {
      if (!doc) return
      useMeetingAnnotationStore.getState().add(doc, stroke)
      sendStroke(channel, doc, stroke)
    },
    [doc, channel],
  )

  const live = useCallback(
    (chunk: LiveChunk) => {
      if (doc) channel?.sendEvent('annotation:live', { shareId: doc, ...chunk })
    },
    [doc, channel],
  )

  const erase = useCallback(
    (ids: string[]) => {
      if (!doc || ids.length === 0) return
      useMeetingAnnotationStore.getState().remove(doc, ids)
      channel?.sendEvent('annotation:erase', { shareId: doc, ids })
    },
    [doc, channel],
  )

  const undo = useCallback(() => {
    if (!doc || !selfId) return
    const mine = (useMeetingAnnotationStore.getState().strokes[doc] ?? []).filter((s) => s.by === selfId)
    const last = mine[mine.length - 1]
    if (!last) return
    useMeetingAnnotationStore.getState().undo(doc, selfId)
    channel?.sendEvent('annotation:erase', { shareId: doc, ids: [last.id] })
  }, [doc, selfId, channel])

  const redo = useCallback(() => {
    if (!doc) return
    const stack = useMeetingAnnotationStore.getState().undone[doc] ?? []
    const last = stack[stack.length - 1]
    if (!last) return
    useMeetingAnnotationStore.getState().redo(doc)
    sendStroke(channel, doc, last)
  }, [doc, channel])

  const clearMine = useCallback(() => {
    if (!doc || !selfId) return
    useMeetingAnnotationStore.getState().clear(doc, selfId)
    channel?.sendEvent('annotation:clear', { shareId: doc })
  }, [doc, selfId, channel])

  /** Everyone's ink on this document — hosts and co-hosts only; the server refuses anyone else. */
  const clearAll = useCallback(() => {
    if (!doc) return
    useMeetingAnnotationStore.getState().clear(doc)
    channel?.sendEvent('annotation:clear', { shareId: doc, scope: 'all' })
  }, [doc, channel])

  const sendView = useCallback(
    (next: AnnotationView) => {
      if (doc) channel?.sendEvent('share:view', { shareId: doc, page: next.page, offset: next.offset })
    },
    [doc, channel],
  )

  return { supported, view, draw, live, erase, undo, redo, clearMine, clearAll, sendView }
}

function sendStroke(channel: ChatChannel | null, doc: string, stroke: Stroke) {
  if (!channel?.request) {
    channel?.sendEvent('annotation:stroke', { shareId: doc, stroke: toWireStroke(stroke) })
    return
  }
  // Asked rather than fired: a stroke the room refused (annotation closed by
  // the host a moment ago, the document just stopped) must not stay on this
  // screen alone, looking shared when nobody else can see it.
  void channel.request('annotation:stroke', { shareId: doc, stroke: toWireStroke(stroke) }).then(({ status }) => {
    if (status === 'error') useMeetingAnnotationStore.getState().remove(doc, [stroke.id])
  })
}
